import { PasswordStrengthConfig, PasswordStrengthResult } from '@/types/passwordStrength';

class PasswordStrengthValidator {
  private config: PasswordStrengthConfig;

  constructor() {
    this.config = {
      passwordLifeTimeInDays: 365,
      passwordAttemptsPerSecond: 500,
      expressions: [
        { regex: /[A-Z]+/, uniqueChars: 26 },
        { regex: /[a-z]+/, uniqueChars: 26 },
        { regex: /[0-9]+/, uniqueChars: 10 },
        { regex: /[!\\?.;,\\@$#*()%~<>{}[\]]+/, uniqueChars: 17 },
      ],
    };
  }

  checkPassword(password: string = ''): PasswordStrengthResult {
    const secondsInADay = 86400;
    let possibilitiesPerLetterInPassword = 0;

    for (const expression of this.config.expressions) {
      if (expression.regex.test(password)) {
        possibilitiesPerLetterInPassword += expression.uniqueChars;
      }
    }

    const totalCombinations = Math.pow(possibilitiesPerLetterInPassword, password.length);
    const crackTimeInSeconds = totalCombinations / this.config.passwordAttemptsPerSecond / 2;
    const crackTimeInDays = crackTimeInSeconds / secondsInADay;
    const timeRatio = crackTimeInDays / this.config.passwordLifeTimeInDays;
    const percentage = Math.min(Math.max(password.length * 5, timeRatio * 100), 100);

    let level: 'useless' | 'weak' | 'medium' | 'strong';
    if (percentage > 90) {
      level = 'strong';
    } else if (percentage > 50) {
      level = 'medium';
    } else if (percentage > 10) {
      level = 'weak';
    } else {
      level = 'useless';
    }

    return {
      percentage: Math.round(percentage),
      level,
      crackTimeInDays,
    };
  }

  getColorClass(level: 'useless' | 'weak' | 'medium' | 'strong'): string {
    switch (level) {
      case 'strong':
        return 'v2-bg-[#154734]';
      case 'medium':
        return 'v2-bg-[#F79009]';
      case 'weak':
        return 'v2-bg-[#F79009]';
      case 'useless':
      default:
        return 'v2-bg-[#D92D20]';
    }
  }
}

export const passwordStrengthValidator = new PasswordStrengthValidator();

export const checkPasswordStrength = (password: string): PasswordStrengthResult => {
  return passwordStrengthValidator.checkPassword(password);
};

export const getPasswordStrengthColorClass = (level: 'useless' | 'weak' | 'medium' | 'strong'): string => {
  return passwordStrengthValidator.getColorClass(level);
};
